import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setStoredToken } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = user
  const [loading, setLoading] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setExpiresAt(data.expires_at || null);
    } catch (e) {
      // Not authenticated — that's fine; render the login screen.
      setUser(false);
      setExpiresAt(null);
      setStoredToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // When any protected request 401s, drop the cached user so <Protected>
  // renders the login screen instead of getting stuck in a redirect loop.
  useEffect(() => {
    const onExpired = () => { setUser(false); setExpiresAt(null); setStoredToken(null); };
    window.addEventListener("legacylink:auth-expired", onExpired);
    return () => window.removeEventListener("legacylink:auth-expired", onExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data);
    setExpiresAt(data.expires_at || null);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) setStoredToken(data.access_token);
    setUser(data);
    setExpiresAt(data.expires_at || null);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed:", e?.message || e);
    }
    setStoredToken(null);
    setUser(false);
    setExpiresAt(null);
  }, []);

  const refreshSession = useCallback(async () => {
    const { data } = await api.post("/auth/refresh");
    if (data.access_token) setStoredToken(data.access_token);
    setExpiresAt(data.expires_at || null);
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, expiresAt, login, register, logout, refresh, refreshSession, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
